import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@demoops/shared-types';
import type { GuideFinding, GuideRegistration } from './types';
import type { GuideFormState } from '@/features/forms/render/FormRenderer';

/**
 * The Guide's only server call.
 *
 * Everything else — the checks, the highlighting, the walkthroughs, "explain
 * this screen" — is computed here in the browser and works offline. This is
 * for questions the app cannot anticipate.
 *
 * The payload is built by `askPayload` below and carries the page's SHAPE
 * only: field names, whether each is filled, row counts, and the app's own
 * findings. No field value is included, and the server drops anything else
 * anyway (AskGuideRequest).
 */

export interface GuideAskPayload {
  question: string;
  route: string;
  module: string;
  screen_title: string;
  screen_purpose: string;
  status: string;
  fields: { name: string; label: string; filled: boolean; row_count?: number }[];
  /**
   * `blocks` matters: without it the model cannot tell a warning from
   * something that actually stops a submit. Asked "why can't I submit?" with a
   * flat list, the live model confidently blamed missing hours — which are
   * advisory. Caught on the real endpoint, not in a test.
   */
  findings: { text: string; blocks: boolean }[];
  /**
   * The page's AUTHORED help copy (the Learn tab's explain sections) — static
   * app text, no record content. Without it the model free-styles policy and
   * can contradict the panel it lives in: asked "do I need a price quote?",
   * the live model said yes while Learn on the same screen said purchasing
   * does the pricing (2026-08-14).
   */
  guidance: string[];
}

/** Server caps (AskGuideRequest): 12 sections, 600 chars each. */
const GUIDANCE_MAX_SECTIONS = 12;
const GUIDANCE_MAX_CHARS = 600;

export function askPayload(
  question: string,
  registration: GuideRegistration,
  form: GuideFormState | null,
  findings: GuideFinding[],
): GuideAskPayload {
  return {
    question,
    route: registration.route,
    module: registration.module,
    screen_title: registration.screenTitle,
    screen_purpose: registration.screenPurpose,
    status: registration.entity.status,
    guidance: registration
      .explain()
      .slice(0, GUIDANCE_MAX_SECTIONS)
      .map((s) => `${s.title}: ${s.body}`.slice(0, GUIDANCE_MAX_CHARS)),
    // Explicitly rebuilt field by field rather than spread, so a new key on
    // GuideFieldState can never ride along into the request by accident.
    fields: (form?.fields ?? []).map((f) => ({
      name: f.name,
      label: f.label,
      filled: f.filled,
      ...(f.rowCount === undefined ? {} : { row_count: f.rowCount }),
    })),
    findings: findings.map((f) => ({ text: f.title, blocks: f.blocksSubmit === true })),
  };
}

export function useGuideAsk() {
  return useMutation({
    mutationFn: async (payload: GuideAskPayload) =>
      (await api.post<ApiResponse<{ reply: string }>>('/api/v1/guide/ask', payload)).data,
  });
}
